/**
 * HUD (PRD §27): sophisticated arcade instrumentation, drawn on a 2-D canvas
 * over the WebGPU output. Minimal during exploration; extra readouts appear
 * contextually.
 *
 * Everything scales off `u(v)`, derived from the shorter screen axis, and
 * respects the safe-area insets. On a phone (`compact`) the whole readout moves
 * to the top so the bottom corners stay clear for thumbs.
 *
 * The menus live in `menu.ts` and are drawn through the same context and the
 * same primitives; this file re-exports their public API because `main.ts`
 * only ever talks to the HUD.
 */

import type { World } from '../game/world';
import type { Input } from '../core/input';
import type { TouchInput } from '../core/touch';
import { NITRO, POWER, WORLD } from '../game/config';
import { STATIONS } from '../game/level';
import { survivalRun, INTERMISSION } from '../game/survival';
import { clamp } from '../core/math';
import {
  Menus,
  AMBER, CYAN, DIM, ICE, MAGENTA, PALE, RED, VIOLET, WHITE, MONO,
  drawBar, drawDiamond, drawFrame, drawLabel, drawSubGlyph, drawValue,
  type MenuCommand, type MenuView, type Screen,
} from './menu';

import type { TextBatch } from '../render/text';
export type { Screen, MenuCommand } from './menu';

/**
 * The debug readout's body colour.
 *
 * Not `DIM`. `DIM` is #1d5b62, a very dark teal chosen to sit quietly against
 * the menu's near-black panels — and the F3 overlay is not on a panel, it is on
 * open water, which is a bright mid-blue. Two colours of similar luminance do
 * not make a contrast, and a diagnostic nobody can read is not a diagnostic.
 * There is a backing behind it now as well; this is the half that goes on top.
 */
const DEBUG_TEXT = '#a8cfd8';

/**
 * `#rrggbb` to the 0..1 quadruple the text batch wants.
 *
 * No colour-space conversion: the swapchain this pass writes is not an sRGB
 * format, so the byte the canvas would have written and the float here mean the
 * same thing. Anything cleverer would make the GPU panel a different colour
 * from every other readout in the game.
 */
function rgba(hex: string, a = 1): [number, number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, a];
}

/** Hoisted so the per-frame HUD path allocates nothing to draw them. */
const KEYS: [string, string][] = [
  ['ARROWS', 'MOVE'],
  ['W/S', 'AIM GUN'],
  ['SPACE', 'CANNON'],
  ['Q', 'TORPEDO'],
  ['C', 'DEPTH'],
  ['E', 'SONAR'],
  ['SHIFT', 'NITRO'],
  ['L', 'LAMP'],
  ['U', 'DOCK'],
  ['TAB', 'CHART'],
  ['ESC', 'PAUSE'],
];
const KEY_W = new Float64Array(KEYS.length);
const ROW_W = new Float64Array(KEYS.length);
const CORNERS: [number, number][] = [[-1, -1], [1, -1], [-1, 1], [1, 1]];
const TAU = Math.PI * 2;

export class Hud {
  private ctx: CanvasRenderingContext2D;
  private w = 1;
  private h = 1;
  private dpr = 1;
  private profile = new Float32Array(96);
  private fpsSmooth = 60;
  private proj = { x: 0, y: 0 };
  showDebug = false;
  /** Set when the device has touch input; drives the on-screen controls. */
  touch: TouchInput | null = null;
  safe = { top: 0, bottom: 0, left: 0, right: 0 };
  /** Mirrored from the audio system so the pause menu can report it. */
  muted = false;
  /** True when a menu is layered over the title rather than over a dive. */
  get overTitle(): boolean { return this.menus.overTitle; }
  /** Options is open over a dive, so a reload would cost the run. */
  get optionsInDive(): boolean { return this.menus.optionsInDive; }
  /** Where the refit wants the hull shown, in viewport fractions, or null. */
  get modelRect() { return this.menus.modelRect; }
  /** The cargo the shop's turntable should be showing, or null for the boat. */
  get showcaseOre() { return this.menus.showcaseOre; }
  /** Mode the last CONTINUE row asked to resume. */
  get resumeMode(): string { return this.menus.resumeMode; }

  /** Mirrored from the shell's settings, for the options screen. */
  frameCap = 60;
  tierPref = 'auto';
  hullPref: 'mk2' | 'model' | 'classic' = 'mk2';
  dofStop = 0;
  ssfx = true;
  ibl = true;
  brightness = 1;
  gfxAo = 3;
  gfxShadow = 3;
  gfxSsr = 3;
  gfxSky = 3;
  gfxAf = 8;
  gfxRes = 1;
  todName = '';
  aaPref = 'auto';
  aaActual = '';
  pendingReload = false;
  tierActual = '';
  musicVol = 0.8;
  sfxVol = 1;
  ambVol = 1;

  private menus: Menus;
  /** Reused every frame; the menus never hold on to it. */
  private view: MenuView = {
    w: 1, h: 1, s: 1, compact: false,
    safe: { top: 0, bottom: 0, left: 0, right: 0 },
  };

  /** Eased HUD-only effects, stepped from the world clock. */
  private lastTime = 0;
  private shieldFlash = 0;
  private nitroPulse = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable for HUD');
    this.ctx = ctx;
    this.menus = new Menus(ctx);
  }

  /**
   * Pixel budget for the overlay, in millions. See `resize`.
   *
   * Separate from the renderer's because text wants more resolution than a
   * shaded surface does — but not unlimited, which is what it had.
   */
  pixelBudget = 4_000_000;

  resize() {
    this.w = this.canvas.clientWidth;
    this.h = this.canvas.clientHeight;
    // THE OVERLAY HAD NO BUDGET AND THE SCENE HAS HAD ONE ALL ALONG.
    //
    // At `devicePixelRatio` 2 on a 1860x1080 window this canvas was 3720x2160 —
    // 8.04 million pixels, cleared and redrawn in full every frame and then
    // composited over the WebGPU canvas by the browser. The 3D render next to
    // it is capped at 2.4 million by `maxPixels`, so the HUD was THREE AND A
    // THIRD TIMES the pixel count of the game it is drawn on top of, and the
    // render-resolution slider could not touch a single one of them.
    //
    // Which is why dropping the scene from 2.4 MP to 0.6 barely moved the GPU:
    // the largest surface in the frame was not being scaled. Same clamp the
    // renderer uses, with a budget of its own because glyph edges show
    // resolution in a way a water surface does not.
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    const over = (this.w * this.h * dpr * dpr) / Math.max(200_000, this.pixelBudget);
    if (over > 1) dpr /= Math.sqrt(over);
    this.dpr = dpr;
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
  }

  // --- menus ----------------------------------------------------------------

  /** 'none' while playing. Assigning is equivalent to `openScreen`. */
  get screen(): Screen {
    return this.menus.screen;
  }

  set screen(s: Screen) {
    this.menus.open(s, this.touch);
  }

  openScreen(s: Screen) {
    this.menus.open(s, this.touch);
  }

  /**
   * Called every frame before `draw`, and only while a screen is open. Returns
   * the command the player picked, or null.
   */
  /** Which mode the player just picked, alongside a `pickMode` command. */
  get pickedMode() { return this.menus.pickedMode; }
  /** Which run card the player just took, alongside a `pickCard` command. */
  get pickedCard() { return this.menus.pickedCard; }

  updateMenu(input: Input, world: World, dt: number): MenuCommand | null {
    this.menus.muted = this.muted;
    this.menus.frameCap = this.frameCap;
    this.menus.tierPref = this.tierPref;
    this.menus.hullPref = this.hullPref;
    this.menus.dofStop = this.dofStop;
    this.menus.ssfx = this.ssfx;
    this.menus.ibl = this.ibl;
    this.menus.brightness = this.brightness;
    this.menus.gfxAo = this.gfxAo;
    this.menus.gfxShadow = this.gfxShadow;
    this.menus.gfxSsr = this.gfxSsr;
    this.menus.gfxSky = this.gfxSky;
    this.menus.gfxAf = this.gfxAf;
    this.menus.gfxRes = this.gfxRes;
    this.menus.todName = this.todName;
    this.menus.aaPref = this.aaPref;
    this.menus.aaActual = this.aaActual;
    this.menus.pendingReload = this.pendingReload;
    this.menus.tierActual = this.tierActual;
    this.menus.musicVol = this.musicVol;
    this.menus.sfxVol = this.sfxVol;
    this.menus.ambVol = this.ambVol;
    return this.menus.update(input, world, dt, this.touch);
  }

  // --- primitives -----------------------------------------------------------

  private label(text: string, x: number, y: number, color = DIM, size = 10, align: CanvasTextAlign = 'left') {
    drawLabel(this.ctx, text, x, y, color, size, align);
  }

  private value(text: string, x: number, y: number, color = CYAN, size = 22, align: CanvasTextAlign = 'left') {
    drawValue(this.ctx, text, x, y, color, size, align);
  }

  private bar(x: number, y: number, w: number, h: number, t: number, color: string, segments = 20) {
    drawBar(this.ctx, x, y, w, h, t, color, segments);
  }

  private frame(x: number, y: number, w: number, h: number) {
    drawFrame(this.ctx, x, y, w, h);
  }

  /**
   * The nitro gauge, deliberately not a third identical bar: swept segments and
   * an afterburner chevron that lights when there is enough left to re-light.
   */
  private chevronBar(x: number, y: number, w: number, h: number, t: number, color: string, segments = 12) {
    const c = this.ctx;
    const slant = h * 0.55;
    const gap = 2;
    const sw = (w - slant - gap * (segments - 1)) / segments;
    const lit = t * segments;
    for (let i = 0; i < segments; i++) {
      const f = clamp(lit - i, 0, 1);
      const sx = x + i * (sw + gap);
      c.fillStyle = f > 0 ? color : 'rgba(255,255,255,0.07)';
      c.globalAlpha = f > 0 ? 0.35 + f * 0.65 : 1;
      c.beginPath();
      c.moveTo(sx + slant, y);
      c.lineTo(sx + slant + sw, y);
      c.lineTo(sx + sw, y + h);
      c.lineTo(sx, y + h);
      c.closePath();
      c.fill();
    }
    c.globalAlpha = 1;
  }

  /** Three stacked chevrons — the afterburner tell next to the nitro gauge. */
  private chevrons(x: number, y: number, size: number, color: string, alpha: number) {
    const c = this.ctx;
    c.strokeStyle = color;
    c.lineWidth = Math.max(1, size * 0.16);
    c.globalAlpha = alpha;
    for (let i = 0; i < 3; i++) {
      const ox = x + i * size * 0.5;
      c.beginPath();
      c.moveTo(ox, y - size * 0.5);
      c.lineTo(ox + size * 0.45, y);
      c.lineTo(ox, y + size * 0.5);
      c.stroke();
    }
    c.globalAlpha = 1;
  }

  /** Countdown ring for a timed powerup. */
  private timerRing(cx: number, cy: number, r: number, t: number, color: string, name: string, u: (v: number) => number) {
    const c = this.ctx;
    c.strokeStyle = 'rgba(255,255,255,0.10)';
    // Thin relative to the radius: the countdown sits inside this ring, and a
    // heavy stroke eats the space the digits need.
    c.lineWidth = Math.max(2, r * 0.14);
    c.beginPath();
    c.arc(cx, cy, r, 0, TAU);
    c.stroke();
    c.strokeStyle = color;
    c.beginPath();
    c.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + clamp(t, 0, 1) * TAU);
    c.stroke();
    this.label(name, cx, cy + r + u(12), color, u(8), 'center');
  }

  /** Remaining hulls, as silhouettes rather than a number. */
  private lives(x: number, y: number, n: number, r: number, color: string) {
    const count = Math.max(0, Math.min(6, n));
    for (let i = 0; i < count; i++) drawSubGlyph(this.ctx, x + i * r * 2.9, y, r, color);
  }

  // --- panels ---------------------------------------------------------------

  /** Terrain / contact profile strip (PRD §14). */
  private sonarStrip(world: World, x: number, y: number, w: number, h: number) {
    const c = this.ctx;
    const n = this.profile.length;
    const span = 620;
    world.sonarProfile(n, span, this.profile);

    c.save();
    c.beginPath();
    c.rect(x, y, w, h);
    c.clip();

    c.fillStyle = 'rgba(4,26,30,0.55)';
    c.fillRect(x, y, w, h);

    // Depth gridlines.
    c.strokeStyle = 'rgba(95,228,224,0.07)';
    c.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const gy = y + (h * i) / 4;
      c.beginPath();
      c.moveTo(x, gy + 0.5);
      c.lineTo(x + w, gy + 0.5);
      c.stroke();
    }

    // Vertical range shown either side of the sub.
    const py = world.player.y;
    const range = 150;
    const toY = (wy: number) => y + h * 0.5 - ((wy - py) / range) * h;

    c.beginPath();
    c.moveTo(x, y + h);
    for (let i = 0; i < n; i++) {
      const px = x + (i / (n - 1)) * w;
      c.lineTo(px, clamp(toY(this.profile[i]), y - 20, y + h + 20));
    }
    c.lineTo(x + w, y + h);
    c.closePath();
    c.fillStyle = 'rgba(46,168,164,0.22)';
    c.fill();
    c.strokeStyle = 'rgba(95,228,224,0.75)';
    c.lineWidth = 1.4;
    c.stroke();

    // Sweep line.
    const sweep = ((world.time * 0.32) % 1) * w;
    const grad = c.createLinearGradient(x + sweep - 40, 0, x + sweep, 0);
    grad.addColorStop(0, 'rgba(95,228,224,0)');
    grad.addColorStop(1, 'rgba(95,228,224,0.18)');
    c.fillStyle = grad;
    c.fillRect(x + sweep - 40, y, 40, h);

    // Sonar contacts, positioned by their offset from the sub (PRD §14).
    const px0 = world.player.x;
    for (const ct of world.sonar.contacts) {
      const ox = (ct.x - px0) / span;
      if (Math.abs(ox) > 0.5) continue;
      const sx = x + w * (0.5 + ox);
      const sy = toY(ct.y);
      if (sy < y - 4 || sy > y + h + 4) continue;
      c.globalAlpha = clamp(ct.strength, 0, 1);
      if (ct.kind === 'mineral') {
        // A DIAMOND, IN THE ORE'S OWN COLOUR. Shape first: the strip already
        // spends a square on enemies and a dot on objectives, and a colour
        // alone would not survive a player who cannot separate amber from
        // copper. The colour is then the second word — eight minerals, eight
        // hues, so a ping says WHICH seam before the label is readable.
        c.fillStyle = ct.colour ?? AMBER;
        c.beginPath();
        c.moveTo(sx, sy - 3.4);
        c.lineTo(sx + 3.4, sy);
        c.lineTo(sx, sy + 3.4);
        c.lineTo(sx - 3.4, sy);
        c.closePath();
        c.fill();
      } else if (ct.kind === 'mechanical') {
        c.fillStyle = RED;
        c.fillRect(sx - 2, sy - 2, 4, 4);
      } else if (ct.kind === 'unknown') {
        c.strokeStyle = VIOLET;
        c.lineWidth = 1.4;
        c.beginPath();
        c.arc(sx, sy, 6 + Math.sin(world.time * 6) * 1.5, 0, TAU);
        c.stroke();
      } else {
        c.fillStyle = AMBER;
        c.beginPath();
        c.arc(sx, sy, 2.6, 0, TAU);
        c.fill();
      }
      c.globalAlpha = 1;
    }

    // Expanding active-ping rings.
    for (const ping of world.sonar.pings) {
      const rr = (ping.r / span) * w;
      c.strokeStyle = `rgba(95,228,224,${clamp(1 - ping.age / ping.life, 0, 1) * 0.5})`;
      c.lineWidth = 1.2;
      c.beginPath();
      c.ellipse(x + w * 0.5 + ((ping.x - px0) / span) * w, toY(ping.y), rr, (rr / span) * 300, 0, 0, TAU);
      c.stroke();
    }

    // The player, centred.
    const cx = x + w * 0.5;
    const cy = toY(py);
    c.fillStyle = AMBER;
    c.beginPath();
    c.moveTo(cx, cy - 4);
    c.lineTo(cx + 4, cy + 3);
    c.lineTo(cx - 4, cy + 3);
    c.closePath();
    c.fill();

    c.restore();
    this.frame(x, y, w, h);
  }

  /**
   * Remaining hit points over every carrier on screen, as a number.
   *
   * A bar says "hurt"; a number says "eleven hundred left, bring the torpedo".
   * The whole point of a carrier is that the player chooses to focus it while a
   * horde is closing, and that choice is only interesting if it is informed —
   * otherwise it is a gamble about whether the thing dies before you do.
   *
   * Drawn under the reticle so a locked carrier keeps its bracket.
   */
  private carrierTags(world: World, u: (v: number) => number) {
    const c = this.ctx;
    for (const e of world.actors.enemies) {
      if (!e.alive || !e.carrier) continue;
      if (!world.project(e.x, e.y + e.radius + 2.5, this.proj)) continue;
      const sx = this.proj.x * this.w;
      const sy = this.proj.y * this.h;
      if (sx < -u(40) || sx > this.w + u(40) || sy < -u(40) || sy > this.h + u(40)) continue;

      const hp = Math.max(0, Math.ceil(e.hp));
      const frac = clamp(e.hp / e.maxHp, 0, 1);
      const txt = String(hp);
      const size = u(15);
      c.font = `700 ${size}px ${MONO}`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      const wpx = c.measureText(txt).width;
      const padX = u(7), h = size + u(6);

      // A plate behind it, because this sits over moving geometry in a fight
      // and unbacked text on rock is unreadable exactly when it matters.
      c.fillStyle = 'rgba(4,12,16,0.72)';
      c.strokeStyle = frac > 0.5 ? AMBER : RED;
      c.lineWidth = 1.4;
      const bx = sx - wpx / 2 - padX, by = sy - h / 2;
      c.beginPath();
      c.rect(bx, by, wpx + padX * 2, h);
      c.fill();
      c.stroke();
      // The drain runs along the bottom edge of the plate — the number is the
      // readout, this is only the glance.
      c.fillStyle = frac > 0.5 ? AMBER : RED;
      c.fillRect(bx, by + h - u(2), (wpx + padX * 2) * frac, u(2));

      c.fillStyle = frac > 0.5 ? '#ffd9a0' : '#ffb0b0';
      c.fillText(txt, sx, sy);
    }
    c.textBaseline = 'alphabetic';
    c.textAlign = 'left';
  }

  /** Locking / locked bracket drawn over the current target. */
  private lockReticle(world: World) {
    const t = world.target;
    const boss = world.targetIsBoss ? world.octopus : null;
    const tx = t ? t.x : boss ? boss.x : 0;
    const ty = t ? t.y : boss ? boss.y : 0;
    if (!t && !boss) return;
    if (!world.project(tx, ty, this.proj)) return;

    const c = this.ctx;
    const sx = this.proj.x * this.w;
    const sy = this.proj.y * this.h;
    const prog = clamp(world.lockProgress, 0, 1);
    const locked = prog >= 1;
    const size = boss ? 58 : 26;
    const r = size * (locked ? 1 : 1.8 - prog * 0.8);
    const col = locked ? AMBER : CYAN;

    c.strokeStyle = col;
    c.lineWidth = locked ? 2 : 1.2;
    const arm = r * 0.42;
    for (const corner of CORNERS) {
      const dx = corner[0], dy = corner[1];
      c.beginPath();
      c.moveTo(sx + dx * r - dx * arm, sy + dy * r);
      c.lineTo(sx + dx * r, sy + dy * r);
      c.lineTo(sx + dx * r, sy + dy * r - dy * arm);
      c.stroke();
    }

    if (locked) {
      c.globalAlpha = 0.55 + 0.45 * Math.abs(Math.sin(world.time * 7));
      this.label('LOCKED', sx, sy + r + 15, AMBER, 9, 'center');
      c.globalAlpha = 1;
    } else {
      this.label(`LOCKING ${Math.round(prog * 100)}%`, sx, sy + r + 15, CYAN, 9, 'center');
      c.strokeStyle = 'rgba(95,228,224,0.75)';
      c.lineWidth = 2;
      c.beginPath();
      c.arc(sx, sy, r + 8, -Math.PI / 2, -Math.PI / 2 + prog * TAU);
      c.stroke();
    }
  }

  /**
   * Run progress across the whole world, with a tick per station. Desktop only:
   * on a phone it would be four pixels of information nobody can read.
   */
  private progressStrip(world: World, W: number, y: number, pad: number, u: (v: number) => number) {
    const c = this.ctx;
    const x = pad;
    const w = W - pad * 2;
    const h = u(4);
    const t = clamp(world.progress, 0, 1);

    c.fillStyle = 'rgba(255,255,255,0.06)';
    c.fillRect(x, y, w, h);
    c.fillStyle = 'rgba(95,228,224,0.45)';
    c.fillRect(x, y, w * t, h);

    for (const s of STATIONS) {
      const sx = x + clamp((s.x - WORLD.startX) / (WORLD.endX - WORLD.startX), 0, 1) * w;
      const on = world.activatedStations.has(s.id);
      c.fillStyle = on ? AMBER : 'rgba(95,228,224,0.22)';
      c.fillRect(sx - 1, y - u(on ? 3 : 1.5), 2, h + u(on ? 6 : 3));
    }

    const hx = x + w * t;
    c.fillStyle = WHITE;
    c.beginPath();
    c.moveTo(hx, y - u(2));
    c.lineTo(hx + u(4), y - u(8));
    c.lineTo(hx - u(4), y - u(8));
    c.closePath();
    c.fill();
  }

  /**
   * Chevron pinned to the screen edge, pointing at the boss.
   *
   * The bar answers "how hurt is it". This answers "where is it", which is the
   * question a player actually has when a health bar appears for something they
   * cannot see. Red rather than the objective's amber, and drawn whenever the
   * contact is off screen — including while it is closing on you from behind.
   */
  private bossArrow(world: World, W: number, H: number, compact: boolean, u: (v: number) => number) {
    const b = world.bossMarker();
    if (!b) return;
    const ok = world.project(b.x, b.y, this.proj);
    const nx = ok ? this.proj.x : b.x > world.player.x ? 1.3 : -0.3;
    const ny = ok ? this.proj.y : 0.5;
    if (ok && nx > 0.06 && nx < 0.94 && ny > 0.1 && ny < 0.9) return;

    const c = this.ctx;
    const m = u(46);
    const sx = clamp(nx * W, m + this.safe.left, W - m - this.safe.right);
    const sy = clamp(
      ny * H,
      this.safe.top + u(compact ? 74 : 58),
      H - this.safe.bottom - u(compact ? 70 : 132),
    );
    const a = Math.atan2(ny * H - H * 0.5, nx * W - W * 0.5);
    const dist = Math.round(Math.hypot(b.x - world.player.x, b.y - world.player.y));

    c.save();
    c.globalAlpha = 0.6 + 0.4 * Math.abs(Math.sin(world.time * 4));
    c.translate(sx, sy);
    c.rotate(a);
    c.strokeStyle = RED;
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(-u(7), -u(9));
    c.lineTo(u(9), 0);
    c.lineTo(-u(7), u(9));
    c.stroke();
    c.restore();
    c.globalAlpha = 1;
    this.label(`CONTACT  ${dist} m`, sx, sy + u(24), RED, u(9), 'center');
  }

  /** Chevron pinned to the screen edge, pointing at the next objective. */
  private objectiveArrow(world: World, W: number, H: number, compact: boolean, u: (v: number) => number) {
    const obj = world.nextObjective();
    if (!obj) return;
    const ok = world.project(obj.x, obj.y, this.proj);
    const nx = ok ? this.proj.x : obj.x > world.player.x ? 1.3 : -0.3;
    const ny = ok ? this.proj.y : 0.5;
    if (ok && nx > 0.06 && nx < 0.94 && ny > 0.1 && ny < 0.9) return;

    const c = this.ctx;
    const m = u(46);
    // Pinned inside a band that clears the top bar and the bottom instrument
    // row, so the marker never lands on top of a readout.
    const sx = clamp(nx * W, m + this.safe.left, W - m - this.safe.right);
    const sy = clamp(
      ny * H,
      this.safe.top + u(compact ? 74 : 58),
      H - this.safe.bottom - u(compact ? 70 : 132),
    );
    const a = Math.atan2(ny * H - H * 0.5, nx * W - W * 0.5);

    c.save();
    c.globalAlpha = 0.55 + 0.45 * Math.abs(Math.sin(world.time * 3));
    c.translate(sx, sy);
    c.rotate(a);
    c.strokeStyle = AMBER;
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(-u(6), -u(9));
    c.lineTo(u(8), 0);
    c.lineTo(-u(6), u(9));
    c.stroke();
    c.restore();

    // The caption flips above the chevron in the lower half of the screen, so
    // it never runs down into the gauges.
    // Was 9px over 8px, which is the size of a unit suffix — on the one marker
    // that says where the game is. The distance is a VALUE, not a label, and
    // the destination has to be legible from across the room.
    const d = Math.hypot(obj.x - world.player.x, obj.y - world.player.y);
    const below = sy < H * 0.55;
    const ly = below ? sy + u(26) : sy - u(28);
    this.value(`${Math.round(d)} m`, sx, ly, AMBER, u(compact ? 13 : 16), 'center');
    if (!compact) this.label(obj.label, sx, below ? ly + u(14) : ly - u(15), PALE, u(10), 'center');
  }

  /** Timed powerups, top-centre. Returns the y the next panel may start at. */
  private powerTimers(world: World, W: number, y: number, u: (v: number) => number): number {
    const p = world.player;
    const over = p.overdrive > 0;
    const aeg = p.aegis > 0;
    if (!over && !aeg) return y;

    const r = u(24);
    const cy = y + r + u(4);
    const gap = u(92);
    let cx = W * 0.5 - (over && aeg ? gap * 0.5 : 0);
    if (over) {
      this.timerRing(cx, cy, r, p.overdrive / POWER.overdrive.time, RED, 'OVERDRIVE', u);
      this.value(Math.ceil(p.overdrive).toString(), cx, cy + u(6), RED, u(17), 'center');
      cx += gap;
    }
    if (aeg) {
      this.timerRing(cx, cy, r, p.aegis / POWER.aegis.time, PALE, 'AEGIS', u);
      this.value(Math.ceil(p.aegis).toString(), cx, cy + u(6), PALE, u(17), 'center');
    }
    return cy + r + u(20);
  }

  /**
   * A word for the gate the boat is standing in.
   *
   * Deliberately quieter than the dock prompt and with no key in it: the gate
   * takes you by being flown into, so an instruction would be describing
   * something that has already happened.
   */
  private gatePrompt(world: World, W: number, H: number, compact: boolean, u: (v: number) => number) {
    const g = world.nearGate;
    if (!g) return;
    const c = this.ctx;
    const y = compact ? H * 0.52 : H * 0.63;
    c.globalAlpha = 0.55 + 0.45 * Math.abs(Math.sin(world.time * 2.2));
    this.label(g.name, W * 0.5, y, '#c9a6ff', u(11), 'center');
    this.value(
      world.player.lane === 0 ? 'A WAY THROUGH' : 'THE WAY BACK',
      W * 0.5, y + u(24), '#c9a6ff', u(15), 'center',
    );
    c.globalAlpha = 1;
  }

  /** Centred prompt while a station is in reach. */
  private dockPrompt(world: World, W: number, H: number, compact: boolean, u: (v: number) => number) {
    const st = world.dockable;
    if (!st) return;
    const c = this.ctx;
    const y = compact ? H * 0.58 : H * 0.70;
    const touchy = !!this.touch?.enabled;
    const text = touchy ? 'TAP DOCK' : 'PRESS U TO REFIT';

    this.label(st.name, W * 0.5, y, CYAN, u(10), 'center');
    c.globalAlpha = 0.6 + 0.4 * Math.abs(Math.sin(world.time * 3.4));
    this.value(text, W * 0.5, y + u(26), AMBER, u(17), 'center');
    c.globalAlpha = 1;

    c.font = `600 ${u(17)}px ${MONO}`;
    const bw = c.measureText(text).width * 0.5 + u(20);
    c.strokeStyle = 'rgba(255,180,84,0.45)';
    c.lineWidth = 1;
    for (let i = 0; i < 2; i++) {
      const dir = i === 0 ? -1 : 1;
      const bx = W * 0.5 + dir * bw;
      c.beginPath();
      c.moveTo(bx - dir * u(6), y + u(6));
      c.lineTo(bx, y + u(6));
      c.lineTo(bx, y + u(32));
      c.lineTo(bx - dir * u(6), y + u(32));
      c.stroke();
    }
  }

  /**
   * The F3 / backtick readout: where the frame is actually going.
   *
   * This exists because the desktop is vsync-capped and its frame rate is flat
   * at 120 whatever you do, so the only way to find a slow zone used to be
   * hand-wrapping `drawIndexed` from a devtools console — which is not
   * something you can do on the phone that is actually dropping frames. Every
   * slow-spot report so far has been geometry or fill in a place nobody
   * expected, and both are now readable in situ.
   *
   * Right column is the GPU's problem, left column is the CPU's. If triangles
   * are flat and the CPU rows are flat and it is still slow, it is fill — which
   * no counter here can see, and which means `fsTerrain` over a full screen.
   */
  /**
   * The performance readout, drawn on the GPU.
   *
   * WHY NOT THE 2-D CANVAS LIKE THE REST OF THE HUD. Because this panel is the
   * one piece of interface whose own cost corrupts what it is for. Thirty lines
   * of `fillText` is thirty text rasterisations a frame, and that time lands in
   * `hud` ms — a number printed ON THIS PANEL. A profiler that adds a
   * millisecond to the frame it is diagnosing reports a frame that does not
   * exist when nobody is looking.
   *
   * The glyph atlas makes it nearly free instead: every line, every bar and the
   * backing behind them accumulate into one vertex buffer and go out in the one
   * draw the text pass was already making. It is the same machinery the ore
   * callouts use, in screen space — which is what `TextBatch.screen` was added
   * for, and is a thing a text renderer should have been able to do anyway.
   *
   * Emitted between `world.draw` and `renderer.render`: the batch is reset by
   * `resetQueue` at the top of the former and flushed at the end of the latter,
   * so that is the whole of the window.
   */
  debugText(world: World, batch: TextBatch, W: number, H: number) {
    if (!this.showDebug) return;
    const compact = (!!this.touch && this.touch.enabled) || H < 460;
    const lines = this.debugLines(world, compact);
    if (!lines.length) return;

    const s = clamp(Math.min(W / 1280, H / 720), 0.58, 1);
    const basePad = Math.round(Math.min(W, H) * 0.035);
    const x = basePad + Math.max(this.safe.left, this.safe.right);
    // Clear of the SCORE / SALVAGE column above it, which runs to about 130
    // display pixels on a desktop.
    const top = this.safe.top + s * (compact ? 104 : 152);

    // AS BIG AS THERE IS ROOM FOR, BETWEEN A FLOOR AND A CEILING.
    //
    // A fixed size cannot work now that every pass is listed. The list is not a
    // fixed length — it is one line per pass that ran, so a frame with the
    // bloom chain and depth of field on has half a dozen more than one without
    // — and a size chosen for the long case is small in the short one while a
    // size chosen for the short case runs off the bottom in the long one.
    //
    // So it asks for a comfortable size and gives ground only when the lines
    // will not fit above the bottom margin. The floor is what it used to be, so
    // this can only ever make the panel bigger. The ceiling is comfort rather
    // than a limit of the atlas — the glyphs are rasterised at 48 px, four
    // times `FONT_SIZE`, so there is headroom well past this before they soften
    // and the cap is only there to stop a short list on a tall screen turning
    // into a billboard.
    const want = s * (compact ? 17 : 22);
    const floor = s * (compact ? 13 : 16.5);
    const room = H - top - s * 20;
    const line = clamp(room / Math.max(1, lines.length), floor, want);
    // The ratio the panel was authored at. Kept rather than recomputed so the
    // gap between lines stays the same shape at every size.
    const size = line * 0.76;

    const style = (col: string) => ({
      colour: rgba(col), size, tracking: s * 0.5, align: 'left' as const,
    });
    // The widest line decides the backing, so it has to be measured before
    // anything is drawn — a panel sized by guess is a panel with lines hanging
    // off the end of it.
    let wide = 0;
    for (const l of lines) wide = Math.max(wide, batch.width(l.t, style(l.col)));

    batch.screen();
    const padX = s * 8, padY = s * 6;
    batch.rect(0, 0, 0, x - padX, top - line + padY, x + wide + padX, top + lines.length * line,
               [0.008, 0.047, 0.067, 0.86]);
    // A hairline down the left, so it reads as an instrument panel rather than
    // as a box that failed to load.
    batch.rect(0, 0, 0, x - padX, top - line + padY, x - padX + Math.max(1, s * 1.5),
               top + lines.length * line, [0.37, 0.89, 0.88, 0.35]);
    let y = top;
    for (const l of lines) { batch.text(0, 0, 0, l.t, x, y, style(l.col)); y += line; }
    batch.world();
  }

  /**
   * The readout's CONTENT, as lines. Nothing here knows how it will be drawn.
   *
   * Split out because it is now drawn on the GPU rather than on the 2-D canvas
   * — see `debugText` — and because the thing being measured must not be the
   * thing doing the measuring. Thirty `fillText` calls a frame landed in `hud`
   * ms, which is a line ON THIS PANEL: the profiler was reporting its own cost
   * as part of the frame it was diagnosing.
   */
  private debugLines(world: World, compact: boolean): { t: string; col: string }[] {
    const r = world.drawStats;
    const cpu = world.cpu;
    const p = world.player;
    const k = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n)}`);
    const lines: { t: string; col: string }[] = [];
    const put = (t: string, col = DEBUG_TEXT) => { lines.push({ t, col }); };

    const tris = r.terrainTris + r.instTris;
    const hot = tris > 260_000;
    put(`${this.fpsSmooth.toFixed(0)} FPS   X ${p.x.toFixed(0)}  Y ${p.y.toFixed(0)}  ` +
      `${world.region.name}`, CYAN);
    put(`TRIS ${k(tris)}   terrain ${k(r.terrainTris)}  inst ${k(r.instTris)}`, hot ? AMBER : DEBUG_TEXT);
    put(`DRAWS ${r.terrainDraws + r.instDraws}   lights ${r.lights}/${r.lightCap}   ` +
      `parts ${k(r.particles)}`);
    if (r.backdropEnabled) {
      put(r.backdropRegions
        ? `BACKDROP  ${r.backdropRegions} region${r.backdropRegions === 1 ? '' : 's'}  `
          + `${k(r.backdropPixels)} px  ${(r.backdropCoverage * 100).toFixed(0)}% of frame`
        : 'BACKDROP  skipped — no visible refractors');
    }
    // The two silent failures in renderer.ts. Anything but zero here means
    // something was dropped this frame and draw order decided what.
    if (r.slotOverflow || r.lightOverflow) {
      put(`DROPPED  instances ${r.slotOverflow}  lights ${r.lightOverflow}`, RED);
    }
    put(`CPU ms  upd ${cpu.update.toFixed(2)}  frame ${cpu.frame.toFixed(2)}  ` +
      `draw ${cpu.draw.toFixed(2)}  render ${cpu.render.toFixed(2)}  ` +
      `hud ${cpu.hud.toFixed(2)}  lamp ${cpu.lamp.toFixed(2)}`);
    // The sum against the real frame. `render` was never counted, so the total
    // could sit at 3 ms of a 29 ms frame and prove nothing. What the gap says:
    // small gap and a big `render` means the main thread is blocking on a GPU
    // that cannot keep up; a big gap means it is idle and waiting on vsync.
    const cpuSum = cpu.update + cpu.frame + cpu.draw + cpu.render + cpu.hud;
    const budget = 1000 / Math.max(1, this.fpsSmooth);
    put(`CPU total ${cpuSum.toFixed(2)} of ${budget.toFixed(2)} ms frame  ` +
      `· unaccounted ${(budget - cpuSum).toFixed(2)}`, cpuSum > budget * 0.7 ? AMBER : DEBUG_TEXT);
    // The line that answers what "unaccounted" was hiding. Two very different
    // situations shared that number — a GPU that cannot keep up, and a thread
    // idling against vsync with the frame already finished — and they want
    // opposite fixes. Measured on the device now, not inferred by subtraction.
    const gpuMs = world.gpuMs;
    if (gpuMs > 0) {
      const idle = Math.max(0, budget - Math.max(cpuSum, gpuMs));
      const verdict = gpuMs > budget * 0.85 ? 'GPU BOUND'
        : cpuSum > budget * 0.7 ? 'CPU BOUND'
        : idle > budget * 0.25 ? 'WAITING ON VSYNC'
        : 'BALANCED';
      put(`GPU ${gpuMs.toFixed(2)} ms  · idle ${idle.toFixed(2)}  · ${verdict}`,
        gpuMs > budget * 0.85 ? AMBER : DEBUG_TEXT);
      // THE BREAKDOWN, WHICH IS THE LINE THAT NAMES SOMETHING TO FIX. The
      // total says the GPU is the problem and stops; the next question is
      // always which pass, and answering it by commenting passes out changes
      // the picture as well as the cost.
      //
      // Sorted by cost and truncated, because a list of twenty passes is a
      // list nobody reads — the tail is a fraction of a millisecond and the
      // head is the whole answer. The share is against the SUM of the passes
      // rather than against the frame total: the two differ by whatever the
      // driver spends between passes, and a percentage that does not add to a
      // hundred invites an hour of chasing the difference.
      const pass = Object.entries(world.gpuPass)
        .filter(([, ms]) => ms > 0.005)
        .sort((a, b) => b[1] - a[1]);
      if (pass.length) {
        const sum = pass.reduce((a, [, ms]) => a + ms, 0);
        // "measured", not "total". The frame figure above IS this sum — there
        // is no separate whole-frame bracket, because a nop pass at the end of
        // the frame does not wait for the frame. Anything the GPU did outside a
        // timed pass is not in either number.
        put(`GPU passes  ${sum.toFixed(2)} ms measured across ${pass.length}`, DEBUG_TEXT);
        // ALL OF THEM. This was capped at eight on the argument that a long
        // list is a list nobody reads — and then the first real profile came
        // back with sixteen passes and "... and 8 under it", which is the
        // question the panel exists to answer, hidden. The tail is where a pass
        // that should be cheap and is not shows up.
        for (const [name, ms] of pass) {
          const share = sum > 0 ? (ms / sum) * 100 : 0;
          // A bar, because the ranking is the point and a column of decimals
          // has to be read to be ranked.
          const bar = '='.repeat(Math.max(1, Math.round(share / 5)));
          put(`  ${name.padEnd(18)} ${ms.toFixed(3)} ms  ${share.toFixed(0).padStart(3)}%  ${bar}`,
            ms > sum * 0.3 ? AMBER : DEBUG_TEXT);
        }
      }
    } else {
      // THREE CASES, NOT ONE. This line used to say "no timestamp-query on this
      // adapter" whenever the number was zero, and the number was zero for a
      // reason that had nothing to do with the adapter — the frame bracket was
      // two empty compute passes, and an empty pass writes no timestamp. So it
      // reported a missing feature on hardware that advertised it, which sent
      // the search to the device request instead of to the passes.
      const t = world.gpuTiming;
      put(!t.supported ? 'GPU  no timestamp-query on this adapter'
        : !t.timing ? 'GPU  timing off — tick "GPU per-pass timing" in the tuner'
        : 'GPU  timestamps enabled but reading zero — see tsNop', DEBUG_TEXT);
    }
    // Live one-shot audio voices, and the worst it has been. These are torn
    // down when they finish, so the live figure drops back to nothing in quiet
    // water. A peak that only ever climbs means they are not being released.
    const va = world.voiceStats;
    put(`VOICES ${va.live}  peak ${va.peak}`, va.live > 60 ? AMBER : DEBUG_TEXT);
    // Live against allocated. A count that climbs while the boat is parked is
    // the thing to chase; if none of them move, the cause is not in here.
    const po = world.poolStats;
    put(`POOLS  en ${po.en}/${po.enTotal}  loot ${po.pk}/${po.pkTotal}  `
      + `shot ${po.pr}/${po.prTotal}  fish ${po.fish}  craters ${po.craters}`);
    // Why the nearest loot cannot be taken, in the collector's own order. See
    // `World.reachStats` — this exists because "I cannot pick that up" has six
    // causes that look identical from a screenshot.
    put(world.reachStats, DEBUG_TEXT);
    put(world.showcaseStats, DEBUG_TEXT);
    put(world.skyStats, DEBUG_TEXT);

    // SLOT OCCUPANCY, count against capacity.
    //
    // The line this replaces printed triangles, which answers "what is
    // expensive" and not "what is about to be dropped". Those are different
    // slots and only the second one causes scenery to vanish. Anything at or
    // near 1.0 is losing instances; the warning in renderer.ts fires the first
    // time it actually does, but by then it has already been invisible for a
    // while, so the headroom is worth being able to WATCH.
    // Slots with a handful of instances are not the ones that overflow, and a
    // capacity of 2 sitting at 1 reads as "50% full" and crowds out the slot
    // that actually matters. Only populations are worth watching.
    const top = r.top.filter((t) => t[4] >= 24).slice(0, compact ? 4 : 6);
    if (top.length) {
      put(top.map(([n, , cnt, use, capacity]) =>
        `${n} ${cnt}/${capacity}${use > 0.9 ? '!' : ''}`).join('  '),
        top[0]![3] > 0.9 ? RED : top[0]![3] > 0.7 ? AMBER : DEBUG_TEXT);
    }
    return lines;
  }

  /**
   * The wave counter, and the fence.
   *
   * The warning is drawn as chevrons closing in from whichever edge is near,
   * because the arena's boundary is a rule rather than a wall and a rule needs
   * to be visible before it is felt. It ramps over the last 14 m.
   */
  private waveStrip(world: World, wave: number, W: number, y: number, u: (v: number) => number) {
    const c = this.ctx;
    const r = survivalRun();
    // Exactly the rhythm SECTOR uses — label at u(10), value at u(34) — because
    // this occupies the same slot. There are only 38 units between the top pad
    // and the sonar strip, and the wave block used to be a four-line stack
    // dropped in at safe.top + 20: WAVE and its number and a second SCORE all
    // drew straight through SECTOR / COASTAL SHELF.
    //
    // The sector line is what gives way. A wave arena is one place and never
    // changes region, so that readout has nothing to say here, while the wave
    // number is the single most important thing on the screen.
    this.label('WAVE', W * 0.5, y + u(10), DIM, u(10), 'center');
    this.value(String(wave), W * 0.5, y + u(34), r.phase === 'gap' ? AMBER : CYAN, u(19), 'center');

    if (r.warn > 0.02) {
      // Edge chevrons, brightest at the boundary.
      c.globalAlpha = Math.min(1, r.warn) * (0.45 + 0.55 * Math.abs(Math.sin(world.time * 5)));
      const px = world.player.x;
      const b = world.rules.content.bounds;
      if (b) {
        const nearWest = px - b.x0 < b.x1 - px;
        const ex = nearWest ? u(26) : W - u(26);
        const dir = nearWest ? 1 : -1;
        c.strokeStyle = RED;
        c.lineWidth = Math.max(2, u(3));
        for (let i = 0; i < 3; i++) {
          const ox = ex + dir * i * u(14);
          c.beginPath();
          c.moveTo(ox, this.h * 0.5 - u(26));
          c.lineTo(ox + dir * u(12), this.h * 0.5);
          c.lineTo(ox, this.h * 0.5 + u(26));
          c.stroke();
        }
      }
      c.globalAlpha = 1;
      if (r.warn > 0.85) this.label('TURN BACK', W * 0.5, this.h * 0.42, RED, u(12), 'center');
    }
  }

  /**
   * Cargo. Two states and they never overlap: a prompt while something is in
   * reach, and a load readout while something is on the line.
   *
   * The readout is deliberately mass-and-strain rather than a cargo manifest.
   * What the player needs mid-haul is how badly the boat is going to handle and
   * how close the line is to parting — the name of the thing is on the toast
   * they got when they hooked it.
   */
  private cargoStrip(
    world: World, W: number, H: number, compact: boolean,
    pad: number, topPad: number, by: number, u: (v: number) => number,
  ) {
    const c = this.ctx;
    const tow = world.tow;

    if (tow.state === 'idle') {
      const near = world.nearCargo;
      if (!near) return;
      // Three centred lines share this column and none of them may overlap:
      // the toast (0.44 compact / 0.62), this prompt, and the dock prompt
      // (0.58 / 0.70). It is also suppressed outright while a toast is up,
      // because the hint that teaches the grapple IS a toast, fired by the
      // same crate at the same moment.
      if (world.toast) return;
      const y = compact ? H * 0.51 : H * 0.54;
      const text = this.touch?.enabled ? 'TAP GRAB' : 'PRESS G TO GRAPPLE';
      this.label(`${near.label}  ·  ${near.mass.toFixed(1)} T`, W * 0.5, y, ICE, u(10), 'center');
      c.globalAlpha = 0.6 + 0.4 * Math.abs(Math.sin(world.time * 3.4));
      this.value(text, W * 0.5, y + u(24), ICE, u(15), 'center');
      c.globalAlpha = 1;
      return;
    }

    if (tow.state === 'flying') return;

    const load = tow.load;
    if (!load) return;

    // On a phone the bottom-left corner belongs to the floating stick, and the
    // readout was being drawn inside its resting circle, under the thumb. In
    // compact everything else has already moved to the top edge, so this does
    // too. On a desktop it goes above the whole gauge stack, anchored off the
    // same `by` the gauges use — the raw pixel constants it used before slid
    // into the DEPTH block as the resolution changed.
    const y = compact ? topPad + u(64) : by - u(58);
    const w = u(compact ? 84 : 116);

    this.label('CARGO', pad, y, DIM, u(10));
    this.value(`${load.mass.toFixed(1)} T`, pad, y + u(22), ICE, u(compact ? 15 : 18));

    // Strain. Amber from half, red from three quarters — the line parts at 1,
    // and a bar that only tells you after the fact is not worth the pixels.
    const col = tow.strain > 0.75 ? RED : tow.strain > 0.5 ? AMBER : ICE;
    this.bar(pad, y + u(30), w, u(6), tow.strain, col, 12);
    if (tow.strain > 0.75) {
      c.globalAlpha = 0.5 + 0.5 * Math.abs(Math.sin(world.time * 9));
      this.label('LINE STRAIN', pad + w + u(8), y + u(36), RED, u(9));
      c.globalAlpha = 1;
    }
  }

  /**
   * Keyboard legend along the bottom. Desktop only — on a phone that strip is
   * where the thumbs live, and the buttons already say what they do.
   */
  private keyMap(W: number, H: number, pad: number, s: number) {
    const c = this.ctx;
    const u = (v: number) => v * s;
    const keyFont = `600 ${u(9)}px ${MONO}`;
    const labFont = `500 ${u(8)}px ${MONO}`;
    const padX = u(6), gap = u(13), boxH = u(15);

    // Measure first so the row can be centred as one unit.
    c.letterSpacing = '1px';
    let total = 0;
    for (let i = 0; i < KEYS.length; i++) {
      c.font = keyFont;
      const kw = c.measureText(KEYS[i][0]).width + padX * 2;
      c.font = labFont;
      const lw = c.measureText(KEYS[i][1]).width;
      KEY_W[i] = kw;
      ROW_W[i] = kw + u(5) + lw;
      total += ROW_W[i] + gap;
    }
    total -= gap;

    let x = W * 0.5 - total / 2;
    const y = H - pad - u(6);
    c.globalAlpha = 0.55;
    for (let i = 0; i < KEYS.length; i++) {
      const kw = KEY_W[i];
      c.strokeStyle = 'rgba(95,228,224,0.30)';
      c.lineWidth = 1;
      c.beginPath();
      // Rounded key cap.
      const rr = u(3);
      const bx = x, byy = y - boxH + u(3);
      c.moveTo(bx + rr, byy);
      c.arcTo(bx + kw, byy, bx + kw, byy + boxH, rr);
      c.arcTo(bx + kw, byy + boxH, bx, byy + boxH, rr);
      c.arcTo(bx, byy + boxH, bx, byy, rr);
      c.arcTo(bx, byy, bx + kw, byy, rr);
      c.closePath();
      c.stroke();
      c.font = keyFont;
      c.fillStyle = CYAN;
      c.textAlign = 'center';
      c.fillText(KEYS[i][0], bx + kw / 2, y - u(1));
      c.font = labFont;
      c.fillStyle = DIM;
      c.textAlign = 'left';
      c.fillText(KEYS[i][1], bx + kw + u(5), y - u(1));
      x += ROW_W[i] + gap;
    }
    c.globalAlpha = 1;
    c.letterSpacing = '0px';
  }

  /** On-screen stick and buttons, drawn from the same layout the input reads. */
  private drawTouch() {
    const t = this.touch;
    // Nothing is drawn until the controls are actually live, so a desktop
    // never shows them.
    if (!t || !t.enabled) return;
    const c = this.ctx;
    const L = t.layout;
    const idle = L.active || t.layout.buttons.some((b) => b.glow > 0.02) ? 1 : 0.72;

    // Stick: outer ring at the anchor, knob offset by the thumb.
    c.lineWidth = 1.5;
    c.strokeStyle = `rgba(95,228,224,${(L.active ? 0.4 : 0.16) * idle})`;
    c.beginPath();
    c.arc(L.stickX, L.stickY, L.stickR, 0, TAU);
    c.stroke();
    c.fillStyle = `rgba(6,32,38,${(L.active ? 0.4 : 0.22) * idle})`;
    c.fill();

    const kx = L.stickX + L.knobX * L.stickR * 0.72;
    const ky = L.stickY + L.knobY * L.stickR * 0.72;
    c.beginPath();
    c.arc(kx, ky, L.stickR * 0.36, 0, TAU);
    c.fillStyle = `rgba(95,228,224,${(L.active ? 0.34 : 0.15) * idle})`;
    c.fill();
    c.strokeStyle = `rgba(150,245,240,${(L.active ? 0.75 : 0.32) * idle})`;
    c.stroke();

    for (const b of L.buttons) {
      // Contextual buttons are hidden until they can do something.
      if (!b.visible) continue;
      const g = b.glow;
      const accent = b.accent ?? '#5fe4e0';
      // A STICK BUTTON HAS TO LOOK LIKE ONE BEFORE IT IS TOUCHED. Nothing
      // else on this cluster moves, so a player who is not told will press
      // FIRE and never discover that sliding it aims the gun. The slot and
      // the two chevrons are the whole instruction, and they are drawn under
      // the button so they read as track rather than as decoration.
      const travel = b.stick || b.stick2 ? b.r * 0.8 : 0;
      if (travel) {
        const end = travel + b.r * 0.3;
        const tip = travel + b.r * 0.52;
        c.lineWidth = 1.5;
        c.strokeStyle = `rgba(95,228,224,${(0.1 + g * 0.3) * idle})`;
        c.beginPath();
        c.moveTo(b.x, b.y - end);
        c.lineTo(b.x, b.y + end);
        if (b.stick2) { c.moveTo(b.x - end, b.y); c.lineTo(b.x + end, b.y); }
        c.stroke();
        for (const s of [-1, 1]) {
          const cy = b.y + s * tip;
          c.beginPath();
          c.moveTo(b.x - b.r * 0.2, cy + s * b.r * 0.17);
          c.lineTo(b.x, cy);
          c.lineTo(b.x + b.r * 0.2, cy + s * b.r * 0.17);
          if (b.stick2) {
            const cx = b.x + s * tip;
            c.moveTo(cx + s * b.r * 0.17, b.y - b.r * 0.2);
            c.lineTo(cx, b.y);
            c.lineTo(cx + s * b.r * 0.17, b.y + b.r * 0.2);
          }
          c.stroke();
        }
      }
      // The knob rides at a fraction of the thumb's travel so it stays inside
      // its own hit circle at full deflection — the target must not move out
      // from under the thumb that is holding it.
      const bx = b.x + b.knobX * travel;
      const by = b.y - b.knobY * travel;
      c.beginPath();
      c.arc(bx, by, b.r, 0, TAU);
      c.fillStyle = `rgba(8,40,46,${(0.26 + g * 0.4) * idle})`;
      c.fill();
      c.lineWidth = 1.5 + g;
      c.strokeStyle = b.accent
        ? accent
        : `rgba(95,228,224,${(0.3 + g * 0.6) * idle})`;
      c.globalAlpha = b.accent ? (0.4 + g * 0.6) * idle : 1;
      c.stroke();
      c.globalAlpha = 1;
      this.label(
        b.label, bx, by + 3.5,
        g > 0.4 ? '#bdfffb' : b.accent ? accent : `rgba(95,228,224,${0.72 * idle})`,
        Math.max(8, b.r * 0.26), 'center',
      );
    }
  }

  // --- main -----------------------------------------------------------------

  /** Wipe it and leave it wiped. For the overlay's own ablation switch. */
  clear() {
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.clearRect(0, 0, this.w, this.h);
  }

  draw(world: World, fps: number) {
    const c = this.ctx;
    this.fpsSmooth += (fps - this.fpsSmooth) * 0.06;
    c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    c.clearRect(0, 0, this.w, this.h);

    const p = world.player;
    const S = p.stats;
    const W = this.w, H = this.h;

    // Everything scales off the shorter axis, and on a phone the whole readout
    // moves to the top so the bottom corners stay clear for thumbs.
    const s = clamp(Math.min(W / 1280, H / 720), 0.58, 1);
    const compact = (!!this.touch && this.touch.enabled) || H < 460;
    const u = (v: number) => v * s;

    // Step the HUD's own easing off the world clock, so nothing animates while
    // the simulation is frozen behind a menu.
    const dt = clamp(world.time - this.lastTime, 0, 0.25);
    this.lastTime = world.time;
    if (p.shieldBroke) this.shieldFlash = 1;
    this.shieldFlash = Math.max(0, this.shieldFlash - dt * 3.2);
    this.nitroPulse = p.nitroOn ? Math.min(1, this.nitroPulse + dt * 6) : Math.max(0, this.nitroPulse - dt * 4);

    const v = this.view;
    v.w = W; v.h = H; v.s = s; v.compact = compact;
    v.safe.top = this.safe.top; v.safe.bottom = this.safe.bottom;
    v.safe.left = this.safe.left; v.safe.right = this.safe.right;

    // The cards that replace the game entirely never show the instrument panel.
    const screen = this.menus.screen;
    if (screen === 'title' || screen === 'gameover' || screen === 'victory' || this.menus.overTitle) {
      this.menus.draw(world, v);
      return;
    }

    const basePad = Math.round(Math.min(W, H) * 0.035);
    const pad = basePad + Math.max(this.safe.left, this.safe.right);
    const topPad = basePad + this.safe.top;
    const hullT = clamp(p.hull / S.maxHull, 0, 1);
    const energyT = clamp(p.energy / S.maxEnergy, 0, 1);
    const shieldT = S.maxShield > 0 ? clamp(p.shield / S.maxShield, 0, 1) : 0;
    const nitroT = clamp(p.nitro / S.nitroMax, 0, 1);
    const nitroReady = p.nitro >= NITRO.minToStart;

    // Contextual touch buttons, decided fresh every frame.
    if (this.touch) {
      this.touch.setVisible('dock', !!world.dockable);
      this.touch.setVisible('charge', S.chargeUnlocked);
      // Visible when there is something to hook, and while there is something
      // on the line to winch or cut — and at no other time.
      this.touch.setVisible('grab', !!world.nearCargo || world.tow.state !== 'idle');
      // Contextual for the same reason the others are: a button that collapses
      // an empty readout only teaches confusion.
      this.touch.setVisible('hold', world.oreHeld.some((n) => n > 0));
    }

    // ── run progress, pinned to the top edge ────────────────────────────
    // A mode with no finale has no progress to show; a mode with waves shows
    // those instead.
    const waveNo = world.rules.wave?.() ?? 0;
    if (!compact && world.rules.content.finale) {
      this.progressStrip(world, W, this.safe.top + u(12), pad, u);
    }

    // ── top bar ──────────────────────────────────────────────────────────
    // One score, in one place. Survival was showing the player's kill score
    // here and the run score again in the centre column, and only the run score
    // means anything in a run — it is the number that banks.
    this.label('SCORE', pad, topPad + u(10), DIM, u(10));
    this.value(
      String(waveNo > 0 ? survivalRun().score : p.score).padStart(6, '0'),
      pad, topPad + u(36), CYAN, u(24),
    );

    if (waveNo > 0) {
      this.waveStrip(world, waveNo, W, topPad, u);
    } else {
      this.label('SECTOR', W * 0.5, topPad + u(10), DIM, u(10), 'center');
      this.value(world.region.name, W * 0.5, topPad + u(34), 'rgba(180,240,238,0.9)', u(15), 'center');
    }

    this.label('TORPEDO', W - pad, topPad + u(10), DIM, u(10), 'right');
    this.value(
      String(p.torpedoes).padStart(2, '0'), W - pad, topPad + u(36),
      p.torpedoes ? CYAN : RED, u(24), 'right',
    );

    if (compact) {
      // Salvage and hulls ride the score line; there is no room for a column.
      c.font = `600 ${u(24)}px ${MONO}`;
      const sw = c.measureText(String(p.score).padStart(6, '0')).width;
      const salX = pad + sw + u(22);
      drawDiamond(c, salX, topPad + u(29), u(6), AMBER);
      this.value(String(world.salvage), salX + u(11), topPad + u(35), AMBER, u(16));
      c.font = `600 ${u(16)}px ${MONO}`;
      this.lives(salX + u(24) + c.measureText(String(world.salvage)).width, topPad + u(29), p.lives, u(5.5), PALE);

      if (S.chargeUnlocked) {
        // 'DC' rather than 'DEPTH CHARGE': the phone has no room for the noun.
        this.label('DC', W - pad, topPad + u(52), DIM, u(8), 'right');
        this.value(String(p.charges).padStart(2, '0'), W - pad - u(18), topPad + u(52), p.charges ? AMBER : DIM, u(13), 'right');
      }
    } else {
      this.label('SALVAGE', pad, topPad + u(58), DIM, u(9));
      drawDiamond(c, pad + u(6), topPad + u(74), u(7), AMBER);
      this.value(String(world.salvage), pad + u(18), topPad + u(80), AMBER, u(19));

      this.label('HULLS', pad, topPad + u(102), DIM, u(9));
      this.lives(pad + u(60), topPad + u(98), p.lives, u(6.5), PALE);

      if (S.chargeUnlocked) {
        this.label('DEPTH CHARGE', W - pad, topPad + u(56), DIM, u(9), 'right');
        this.value(String(p.charges).padStart(2, '0'), W - pad, topPad + u(78), p.charges ? AMBER : DIM, u(19), 'right');
      }
      this.label(S.cannon.name, W - pad, topPad + u(S.chargeUnlocked ? 98 : 60), DIM, u(9), 'right');
    }

    // THE HOLD IS NOT DRAWN HERE. It is real ore now — the shard meshes
    // themselves, turning, six metres off the lens with the display rig on
    // them — which this canvas cannot draw at any price. See
    // `World.drawOreIcons`. Their counts ride the GPU text batch anchored at
    // each icon, so glyph and crystal are placed by the same matrix.

    // ── mining, while the boat is holding station over a seam ───────────
    //
    // A BAR RATHER THAN A NUMBER, because the question being asked while it
    // runs is "how much longer", and a count answers "how much so far". Under
    // the boat and centred, which is where the player is already looking — a
    // readout in a corner would mean watching the corner instead of the rock
    // the beam is on.
    {
      const mn = world.miningState;
      if (mn) {
        const bw = Math.min(u(260), W * 0.26);
        const bx = W * 0.5 - bw / 2;
        const by = H * 0.5 + u(compact ? 54 : 74);
        this.label(`MINING  ·  ${mn.label}`, W * 0.5, by - u(9), mn.colour, u(9), 'center');
        this.bar(bx, by, bw, u(7), mn.taken / Math.max(1, mn.total), mn.colour, mn.total);
        this.label(`${mn.taken} / ${mn.total}`, W * 0.5, by + u(20), PALE, u(9), 'center');
      }
    }

    // ── sonar strip, top-centre ─────────────────────────────────────────
    const stripW = Math.min(u(420), W * (compact ? 0.3 : 0.34));
    const stripH = u(compact ? 58 : 78);
    const stripY = topPad + u(48);
    this.sonarStrip(world, W * 0.5 - stripW / 2, stripY, stripW, stripH);
    // The strip's caption is decoration; the intermission clock is not. During
    // a gap the countdown takes the slot, which also puts it directly under the
    // wave number it belongs to.
    const run = waveNo > 0 ? survivalRun() : null;
    if (run && run.phase === 'gap') {
      const left = Math.max(0, Math.ceil(INTERMISSION - run.t));
      this.label(
        left > 0 ? `NEXT WAVE IN ${left}` : 'INCOMING',
        W * 0.5, stripY + stripH + u(13), AMBER, u(9), 'center',
      );
    } else {
      this.label('TERRAIN / SONAR', W * 0.5, stripY + stripH + u(13), DIM, u(9), 'center');
    }

    // ── the mode's standing instruction ─────────────────────────────────
    //
    // Under the strip, on screen for as long as it is true. Expedition
    // announced its contract in a toast and then showed nothing at all, so
    // three seconds after taking a job there was no way to learn what the job
    // was — the mode was unreadable, not difficult.
    // Everything in it used to be nine pixels of centred text, which is the
    // same weight the game gives to the word "SONAR" — so the one line telling
    // you what to do read as a caption on the strip above it. Three sizes now,
    // in the order the eye should take them: the destination, the distance, and
    // the context. And a ground behind it, because pale type over lit water and
    // drifting medusae is unreadable no matter how large it is.
    const status = world.rules.status?.(world);
    if (status) {
      const c = this.ctx;
      const top = stripY + stripH + u(compact ? 22 : 26);
      const big = u(compact ? 26 : 34);
      const hasHead = !!status.headline;
      // Explicit line heights rather than eyeballed offsets. The caption under
      // the headline has to clear a 34-pixel numeral's DESCENDER, not its
      // baseline, and a gap chosen against the baseline puts the two on top of
      // each other — which is exactly what the first attempt at this did.
      const titleH = u(compact ? 15 : 18);
      const headH = big * 0.92;
      const capH = u(compact ? 13 : 16);
      const lineH = u(compact ? 13 : 15);
      const padT = u(compact ? 13 : 16), padB = u(compact ? 11 : 14);
      const pw = Math.min(u(compact ? 250 : 340), W * 0.64);
      const ph = padT + titleH
        + (hasHead ? headH : 0)
        + (status.headlineLabel ? capH : 0)
        + status.lines.length * lineH + padB;
      const px = W * 0.5 - pw / 2;

      // A SLAB WITH EDGES IS A SLAB YOU CAN SEE.
      //
      // This was a flat translucent rectangle, and the moment the searchlight's
      // volumetrics passed behind it the panel cut a hard-edged hole out of the
      // light — a straight vertical border down the middle of a lit cone, which
      // is the one thing in the frame that cannot be part of the ocean.
      //
      // An ellipse that fades to nothing has no border to notice. It darkens
      // the middle, where the type is, and stops mattering before it reaches
      // anywhere the eye can find a line. Drawn in a scaled space so one radial
      // gradient covers a panel of any proportion.
      c.save();
      c.translate(px + pw / 2, top + ph / 2);
      c.scale(pw / 2, ph / 2);
      const g = c.createRadialGradient(0, 0, 0, 0, 0, 1);
      g.addColorStop(0, 'rgba(2,11,17,0.72)');
      g.addColorStop(0.55, 'rgba(2,11,17,0.58)');
      g.addColorStop(0.85, 'rgba(2,11,17,0.18)');
      g.addColorStop(1, 'rgba(2,11,17,0)');
      c.fillStyle = g;
      c.fillRect(-1.3, -1.3, 2.6, 2.6);
      c.restore();

      let sy = top + padT + titleH * 0.72;
      this.label(status.title, W * 0.5, sy, AMBER, u(compact ? 11 : 13), 'center');
      if (hasHead) {
        sy += headH;
        this.value(status.headline!, W * 0.5, sy, CYAN, big, 'center');
      }
      if (status.headlineLabel) {
        sy += capH;
        this.label(status.headlineLabel, W * 0.5, sy, DIM, u(compact ? 8.5 : 9.5), 'center');
      }
      for (const line of status.lines) {
        sy += lineH;
        this.label(line, W * 0.5, sy, PALE, u(compact ? 9 : 11), 'center');
      }
    }

    // ── timed powerups, under the strip ─────────────────────────────────
    const afterPower = this.powerTimers(world, W, stripY + stripH + u(20), u);

    let by: number;
    if (compact) {
      // Gauges tuck under the score, clear of both thumbs.
      by = topPad + u(58);
      this.label('DEPTH', pad, by, DIM, u(9));
      this.value(`${Math.round(p.depth)} m`, pad, by + u(22), CYAN, u(21));

      this.label('HULL', pad, by + u(44), DIM, u(9));
      if (S.maxShield > 0) this.shieldBar(pad, by + u(49), u(140), u(4), shieldT);
      this.bar(pad, by + u(56), u(140), u(9), hullT, hullT > 0.35 ? CYAN : RED, 14);

      this.label('ENERGY', pad, by + u(80), DIM, u(9));
      this.bar(pad, by + u(85), u(140), u(6), energyT, AMBER, 14);

      this.nitroGauge(pad, by + u(110), u(140), u(7), nitroT, nitroReady, u);

      const ry = topPad + u(72);
      this.label('VELOCITY', W - pad, ry, DIM, u(9), 'right');
      this.value(`${p.speed.toFixed(1)} m/s`, W - pad, ry + u(24), CYAN, u(19), 'right');
      const rdy = world.sonar.cooldown <= 0 && p.energy >= S.sonarCost;
      this.label(rdy ? 'SONAR READY' : 'SONAR CHARGING', W - pad, ry + u(44), rdy ? CYAN : DIM, u(9), 'right');
      this.label(S.cannon.name, W - pad, ry + u(60), DIM, u(9), 'right');
      by = H - basePad - this.safe.bottom - u(58);
    } else {
      by = H - basePad - this.safe.bottom - u(76);
      this.label('DEPTH', pad, by, DIM, u(10));
      this.value(`${Math.round(p.depth)}`, pad, by + u(30), CYAN, u(30));
      const dw = c.measureText(`${Math.round(p.depth)}`).width;
      this.value('m', pad + dw + u(8), by + u(30), DIM, u(15));

      const gx = pad + u(130), gw = u(168);
      this.label('HULL', gx, by - u(2), DIM, u(10));
      if (S.maxShield > 0) this.shieldBar(gx, by + u(2), gw, u(4), shieldT);
      this.bar(gx, by + u(8), gw, u(11), hullT, hullT > 0.35 ? CYAN : RED, 16);

      this.label('ENERGY', gx, by + u(32), DIM, u(10));
      this.bar(gx, by + u(36), gw, u(7), energyT, AMBER, 16);

      this.nitroGauge(gx + gw + u(26), by + u(8), u(130), u(11), nitroT, nitroReady, u);

      this.label('VELOCITY', W - pad, by, DIM, u(10), 'right');
      this.value(`${p.speed.toFixed(1)}`, W - pad - u(34), by + u(30), CYAN, u(30), 'right');
      this.value('m/s', W - pad, by + u(30), DIM, u(15), 'right');

      this.label('LAMP', W - pad, by + u(52), DIM, u(9), 'right');
      this.value(p.lightOn ? 'ON' : 'OFF', W - pad - u(44), by + u(52), p.lightOn ? CYAN : DIM, u(11), 'right');

      const rdy = world.sonar.cooldown <= 0 && p.energy >= S.sonarCost;
      this.label('SONAR', W - pad, by + u(68), DIM, u(9), 'right');
      this.value(rdy ? 'READY' : 'CHARGING', W - pad - u(44), by + u(68), rdy ? CYAN : DIM, u(11), 'right');
    }

    // ── target lock (PRD §13) ───────────────────────────────────────────
    this.carrierTags(world, u);
    this.lockReticle(world);

    // ── off-screen sonar contact markers ────────────────────────────────
    for (const ct of world.sonar.contacts) {
      if (!world.project(ct.x, ct.y, this.proj)) continue;
      const inside = this.proj.x > 0.02 && this.proj.x < 0.98 && this.proj.y > 0.02 && this.proj.y < 0.98;
      const sx = clamp(this.proj.x, 0.03, 0.97) * W;
      const sy = clamp(this.proj.y, 0.06, 0.94) * H;
      const tint = ct.colour
        ?? (ct.kind === 'mechanical' ? RED : ct.kind === 'unknown' ? VIOLET : AMBER);
      // AN ANCHORED CALLOUT FOR ORE, WITH A LEADER TO THE ROCK IT IS ABOUT.
      //
      // A label floating anywhere near a mark is a caption; a label on the end
      // of a line is an annotation, and the difference matters here because a
      // ping can put four of these on screen at once. Without the leader the
      // player has to guess which text belongs to which diamond, and the
      // guess is wrong exactly when it costs something — two seams a few
      // metres apart, one of them abyssal crystal.
      //
      // The elbow is the classic survey shape: a diagonal off the subject,
      // then a short horizontal run under the text, so the eye is handed from
      // one to the other without the line ever crossing the label.
      // ORE CALLOUTS ARE NOT DRAWN HERE ANY MORE. They are glyph quads in the
      // WebGPU layer now — see `World.drawOreCallouts` — because that puts the
      // anchor through the same matrix as the geometry it points at, on the
      // same hardware, in the same frame. This canvas sits ON TOP of that
      // layer, so an on-screen ore contact must fall through to nothing here
      // or the label would be drawn twice, half a frame apart.
      //
      // Off-screen ore keeps the edge marker below: that is a different job —
      // an arrow saying "over there" rather than a label naming a thing.
      if (ct.kind === 'mineral' && inside) continue;
      c.globalAlpha = clamp(ct.strength, 0, 1) * (inside ? 0.5 : 0.85);
      c.strokeStyle = tint;
      c.lineWidth = 1.2;
      c.beginPath();
      c.arc(sx, sy, inside ? 13 : 8, 0, TAU);
      c.stroke();
      if (!inside && ct.label) this.label(ct.label, sx, sy - 13, tint, 8, 'center');
      c.globalAlpha = 1;
    }

    // ── next objective ──────────────────────────────────────────────────
    this.objectiveArrow(world, W, H, compact, u);
    this.bossArrow(world, W, H, compact, u);

    // ── boss health (PRD §18) ───────────────────────────────────────────
    const boss = world.bossState();
    let bossBarBottom = 0;
    if (boss) {
      const bw = Math.min(u(560), W * 0.42);
      const bx = W * 0.5 - bw / 2;
      const byy = compact ? afterPower + u(18) : H - pad - u(120);
      this.label(boss.name, W * 0.5, byy - u(8), VIOLET, u(9), 'center');
      this.bar(bx, byy, bw, u(9), clamp(boss.health, 0, 1), VIOLET, 40);
      if (boss.sub) this.label(boss.sub, W * 0.5, byy + u(22), DIM, u(8), 'center');
      bossBarBottom = byy + u(30);
    }
    if (world.bossBannerTime > 0) {
      const k = Math.min(1, world.bossBannerTime / 0.6);
      c.globalAlpha = k;
      // The banner and the bar both want the upper middle. Whichever is on
      // screen first owns it; the banner steps below the bar rather than
      // printing straight through it.
      const by = Math.max(H * 0.3, bossBarBottom + u(30));
      this.value(world.bossBannerLabel || 'CONTACT', W * 0.5, by, VIOLET, u(34), 'center');
      c.globalAlpha = 1;
    }

    // ── docking ─────────────────────────────────────────────────────────
    this.dockPrompt(world, W, H, compact, u);
    this.gatePrompt(world, W, H, compact, u);
    this.cargoStrip(world, W, H, compact, pad, topPad, by, u);

    // ── contextual toast ────────────────────────────────────────────────
    if (world.toast) {
      const k = clamp(world.toast.time / 0.5, 0, 1);
      c.globalAlpha = k;
      const ty = compact ? H * 0.44 : H * 0.62;
      this.label(world.toast.text, W * 0.5, ty, world.toast.color, u(10), 'center');
      this.value(world.toast.sub, W * 0.5, ty + u(24), CYAN, u(17), 'center');
      c.globalAlpha = 1;
    }

    // ── low-hull warning ────────────────────────────────────────────────
    if (hullT < 0.34 && world.deathTimer <= 0) {
      const blink = 0.45 + 0.55 * Math.abs(Math.sin(world.time * 5));
      c.globalAlpha = blink;
      // Clear of the boss bar, which owns the strip just above the gauges.
      this.value('HULL INTEGRITY CRITICAL', W * 0.5, compact ? H * 0.36 : H - pad - u(160), RED, u(14), 'center');
      c.globalAlpha = 1;
      // The blinking red frame that used to ring the viewport here is gone. It
      // was the loudest thing on screen for a state the player is already told
      // about three other ways — the hull bar goes red, the text blinks, and
      // the damage is why they are at 34% in the first place — and it framed
      // the one thing they need to look at while doing it. The warning stays;
      // the border around the game does not.
    }

    // ── death (respawn is automatic; there is nothing to press) ─────────
    if (world.deathTimer > 0) {
      c.fillStyle = 'rgba(2,8,12,0.62)';
      c.fillRect(0, 0, W, H);
      this.value('HULL BREACH', W * 0.5, H * 0.44, RED, u(46), 'center');
      if (p.lives >= 0) {
        this.label('SPARE HULLS', W * 0.5, H * 0.44 + u(30), DIM, u(10), 'center');
        this.lives(W * 0.5 - (Math.min(6, Math.max(0, p.lives)) - 1) * u(13), H * 0.44 + u(52), p.lives, u(9), PALE);
      } else {
        this.label('SALVAGE TEAM INBOUND', W * 0.5, H * 0.44 + u(34), AMBER, u(12), 'center');
      }
    }

    if (compact) {
      if (this.touch && screen === 'none') this.drawTouch();
    } else {
      this.keyMap(W, H, basePad + this.safe.bottom, s);
    }


    // Menus that sit over the running game.
    this.menus.draw(world, v);
  }

  /** The shield: a thinner, colder bar layered directly above the hull. */
  private shieldBar(x: number, y: number, w: number, h: number, t: number) {
    const c = this.ctx;
    if (this.shieldFlash > 0) {
      // A collapse is the single most important thing on the panel for a beat.
      c.globalAlpha = this.shieldFlash;
      c.fillStyle = '#ffffff';
      c.fillRect(x - 2, y - 2, w + 4, h + 4);
      c.globalAlpha = 1;
    }
    this.bar(x, y, w, h, t, t > 0 ? ICE : 'rgba(106,184,255,0.25)', 20);
  }

  /** Nitro: swept segments, a chevron tell, and a label that dims when spent. */
  private nitroGauge(x: number, y: number, w: number, h: number, t: number, ready: boolean, u: (v: number) => number) {
    const lit = ready ? MAGENTA : 'rgba(255,106,213,0.35)';
    this.label('NITRO', x, y - u(6), ready ? MAGENTA : DIM, u(9));
    this.chevronBar(x, y, w - u(22), h, t, lit, 12);
    this.chevrons(
      x + w - u(20), y + h * 0.5, h * 1.1, lit,
      ready ? 0.5 + this.nitroPulse * 0.5 : 0.25,
    );
  }
}
